import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, Clock, Target, CheckCircle, PlayCircle, RotateCcw, Lock, ShoppingCart, Trophy } from 'lucide-react';

export default function TestSeriesDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addItem, isInCart } = useCart();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    try {
      const { data } = await api.get(`/test-series/${id}`);
      setSeries(data);
      api.get(`/leaderboard/${id}`).then(r => setLeaderboard(r.data)).catch(() => {});
      if (user) {
        const accessRes = await api.get(`/student/check-access/${id}`);
        setHasAccess(accessRes.data.has_access);
        if (accessRes.data.has_access) {
          const testsRes = await api.get(`/student/series/${id}/tests`);
          setTests(testsRes.data);
        }
      }
    } catch (err) {
      toast.error('Failed to load test series');
    } finally {
      setLoading(false);
    }
  }

  async function handlePurchase() {
    if (!user) { navigate('/login'); return; }
    setPurchasing(true);
    try {
      await api.post(`/student/purchase/${id}`);
      toast.success('Successfully enrolled!');
      setHasAccess(true);
      const testsRes = await api.get(`/student/series/${id}/tests`);
      setTests(testsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  }

  async function handleStartTest(testId) {
    try {
      const { data } = await api.post(`/student/tests/${testId}/start`);
      navigate(`/test/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start test');
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-4 border-[#7551e8] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!series) return (
    <div className="max-w-7xl mx-auto px-4 py-12 text-center">
      <p className="text-lg text-[#52525B]">Test series not found</p>
    </div>
  );

  const effectivePrice = series.discount_price > 0 && series.discount_price < series.price ? series.discount_price : series.price;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="test-series-detail">
      {/* Header */}
      <div className="grid lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-2 space-y-4">
          {series.category_name && (
            <Badge className="bg-[#F4F0FF] text-[#7551e8] hover:bg-[#F4F0FF] border-0">{series.category_name}</Badge>
          )}
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-[#0A0A0A]" data-testid="series-title">{series.title}</h1>
          {series.description && <p className="text-[#52525B] leading-relaxed">{series.description}</p>}
          <div className="flex flex-wrap gap-4 text-sm text-[#52525B]">
            <span className="flex items-center gap-1.5"><BookOpen size={14} /> {series.total_tests || 0} Tests</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {series.validity_months || 12} Months Access</span>
          </div>
        </div>

        <Card className="p-6 border border-zinc-200 h-fit" data-testid="series-purchase-card">
          {series.thumbnail_url && (
            <img src={series.thumbnail_url} alt={series.title} className="w-full aspect-video object-cover rounded-lg mb-4" />
          )}
          <div className="mb-4">
            {effectivePrice > 0 ? (
              <div className="flex items-baseline gap-2">
                <span className="font-['Outfit'] text-3xl font-semibold">&#8377;{effectivePrice}</span>
                {series.discount_price > 0 && series.discount_price < series.price && (
                  <span className="price-original text-lg">&#8377;{series.price}</span>
                )}
              </div>
            ) : (
              <span className="font-['Outfit'] text-3xl font-semibold text-[#10B981]">Free</span>
            )}
          </div>
          {hasAccess ? (
            <Button className="w-full bg-[#10B981] hover:bg-green-600 text-white py-5" disabled data-testid="enrolled-badge">
              <CheckCircle size={16} className="mr-2" /> Enrolled
            </Button>
          ) : (
            <div className="space-y-2">
              <Button className="w-full bg-[#7551e8] hover:bg-[#5e3dc7] text-white py-5"
                onClick={handlePurchase} disabled={purchasing} data-testid="purchase-btn">
                {purchasing ? 'Processing...' : effectivePrice > 0 ? `Buy Now - Rs.${effectivePrice}` : 'Enroll Free'}
              </Button>
              {effectivePrice > 0 && !isInCart(id) && (
                <Button variant="outline" className="w-full py-5 border-[#7551e8] text-[#7551e8] hover:bg-[#F4F0FF]"
                  onClick={() => addItem(series)} data-testid="add-to-cart-btn">
                  <ShoppingCart size={16} className="mr-2" /> Add to Cart
                </Button>
              )}
              {isInCart(id) && (
                <Button variant="outline" className="w-full py-5 border-green-500 text-green-600" disabled>
                  <CheckCircle size={16} className="mr-2" /> In Cart
                </Button>
              )}
            </div>
          )}
          <p className="text-xs text-center text-zinc-400 mt-2">Secure payment via Razorpay</p>
        </Card>
      </div>

      {/* Tests List */}
      {hasAccess && (
        <div data-testid="tests-list">
          <h2 className="font-['Outfit'] text-xl font-medium text-[#0A0A0A] mb-4">Tests in this Series</h2>
          {tests.length === 0 ? (
            <Card className="p-8 text-center border border-zinc-200">
              <p className="text-[#52525B]">No tests available yet.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {tests.map((t, i) => (
                <Card key={t.id} className="p-4 border border-zinc-200 hover:border-[#7551e8]/30 transition-colors" data-testid={`test-item-${t.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F4F0FF] flex items-center justify-center text-sm font-semibold text-[#7551e8]">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-medium text-[#0A0A0A]">{t.title}</h3>
                        <div className="flex gap-3 text-xs text-[#52525B] mt-0.5">
                          <span>{t.question_count || 0} Qs</span>
                          <span>{t.duration_minutes} min</span>
                          <span>{t.total_marks} marks</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.status === 'completed' ? (
                        <>
                          <Badge className="bg-green-50 text-green-700 border-0">Score: {t.score?.toFixed(1)}</Badge>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/results/${t.attempt_id}`)} data-testid={`view-result-${t.id}`}>
                            View Result
                          </Button>
                          <Button size="sm" className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => handleStartTest(t.id)} data-testid={`retake-${t.id}`}>
                            <RotateCcw size={12} className="mr-1" /> Retake
                          </Button>
                        </>
                      ) : t.status === 'in_progress' ? (
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate(`/test/${t.attempt_id}`)} data-testid={`resume-${t.id}`}>
                          <PlayCircle size={12} className="mr-1" /> Resume
                        </Button>
                      ) : t.question_count > 0 ? (
                        <Button size="sm" className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => handleStartTest(t.id)} data-testid={`start-test-${t.id}`}>
                          <PlayCircle size={12} className="mr-1" /> Start Test
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-zinc-400"><Lock size={12} className="mr-1" /> No Questions</Badge>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {!hasAccess && !user && (
        <Card className="p-8 text-center border border-zinc-200 mt-8">
          <Lock size={32} className="mx-auto mb-3 text-zinc-300" />
          <p className="text-lg font-medium mb-2">Sign in to access tests</p>
          <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={() => navigate('/login')}>Sign In</Button>
        </Card>
      )}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="mt-8" data-testid="leaderboard-section">
          <h2 className="font-['Outfit'] text-xl font-medium text-[#0A0A0A] mb-4 flex items-center gap-2">
            <Trophy size={20} className="text-[#7551e8]" /> Top Performers
          </h2>
          <Card className="border border-zinc-200 overflow-hidden">
            <div className="divide-y divide-zinc-100">
              {leaderboard.map((entry) => (
                <div key={entry.rank} className={`flex items-center gap-4 px-5 py-3 ${entry.rank <= 3 ? 'bg-[#FEFCE8]' : ''}`} data-testid={`lb-rank-${entry.rank}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                    entry.rank === 2 ? 'bg-zinc-300 text-zinc-700' :
                    entry.rank === 3 ? 'bg-orange-300 text-orange-800' :
                    'bg-zinc-100 text-zinc-500'
                  }`}>
                    {entry.rank}
                  </div>
                  {entry.profile_image ? (
                    <img src={entry.profile_image} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#F4F0FF] flex items-center justify-center text-xs font-semibold text-[#7551e8]">
                      {entry.user_name?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#0A0A0A] truncate">{entry.user_name}</p>
                    <p className="text-xs text-[#52525B]">{entry.attempts} attempt{entry.attempts > 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-['Outfit'] font-semibold text-[#0A0A0A]">{entry.best_score?.toFixed(1)}</p>
                    <p className="text-xs text-[#7551e8]">{entry.percentile?.toFixed(0)}%ile</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
